import React, { Dispatch, SetStateAction } from "react";
import {
  ResortMappingObject,
  RTPMappingProduct,
  SiriuswareMappingProduct,
} from "./productMappings";
import { RtpProductRow } from "./RtpProductRow";
import { SwProductRow } from "./SwProductRow";
import { DynamicTable } from "./DynamicTable";
import { rtpColumnConfig, swColumnConfig } from "./columnConfig";
import { IAppState } from "../core/redux/IAppState";
import { useSelector } from "react-redux";
import { getResource } from "../language/resources/getResource";
import { hasFailed, isBusy, LoadStatus } from "./productMappingLoadStatus";
import { isResortMappingErrorMissingSalesChannel } from "./getResortMappingErrorAction";

interface Props {
  resortMappingObject: ResortMappingObject;
  locked: boolean;
  changedIds: string[];
  setChangedIds: Dispatch<SetStateAction<string[]>>;
  isLoadingData: boolean;
  showHydrationErrors: boolean;
  mappingStatus: LoadStatus;
  hydrationStatus: LoadStatus;
}

export const ProductMappingTable = (props: Props) => {
  const isRtp = props.resortMappingObject.posProvider === 'RTP';
  const hasFulfillmentSeason = !!props.resortMappingObject.productMappings[0]?.fulfillmentSeason;

  const rtpMappingEntitlements = props.resortMappingObject.productMappings as unknown as RTPMappingProduct[];
  const swMappingEntitlements = props.resortMappingObject.productMappings as unknown as SiriuswareMappingProduct[];

  const resources = useSelector((state: IAppState) => state.locale);
  const missingSalesChannel = useSelector((state: IAppState) =>
    isResortMappingErrorMissingSalesChannel(state, state.selectedResort.resortAbbreviation));

  // A check that failed to load gets a red triangle next to its column's title
  const failedToLoad = getResource(resources, 'PRODUCT_MAPPING_PAGE_COLUMN_LOAD_FAILED');
  const columnErrors = {
    errorDescription: hasFailed(props.mappingStatus)
      ? (missingSalesChannel ? getResource(resources, 'PRODUCT_MAPPING_PAGE_MISSING_SALES_CHANNEL') : failedToLoad)
      : undefined,
    hydrationErrors:  hasFailed(props.hydrationStatus) ? failedToLoad : undefined,
  };
  const loadingColumns = [
    ...(isBusy(props.mappingStatus) ? ['errorDescription'] : []),
    ...(isBusy(props.hydrationStatus) ? ['hydrationErrors'] : []),
  ];

  const renderRtpRow = (entitlement: RTPMappingProduct) => (
    <RtpProductRow
      key={entitlement.id}
      locked={props.locked}
      entitlement={entitlement}
      changedIds={props.changedIds}
      setChangedIds={props.setChangedIds}
      showHydrationErrors={props.showHydrationErrors}
      mappingStatus={props.mappingStatus}
      hydrationStatus={props.hydrationStatus}
    />
  );

  const renderSwRow = (entitlement: SiriuswareMappingProduct) => (
    <SwProductRow
      key={entitlement.id}
      locked={props.locked}
      entitlement={entitlement}
      changedIds={props.changedIds}
      setChangedIds={props.setChangedIds}
      showHydrationErrors={props.showHydrationErrors}
      mappingStatus={props.mappingStatus}
      hydrationStatus={props.hydrationStatus}
    />
  );

  return (
    <div>
      {
        isRtp ? 
        <DynamicTable
          key="rtpProductMappingTable"
          columns={rtpColumnConfig(resources, hasFulfillmentSeason, props.showHydrationErrors)}
          data={rtpMappingEntitlements}
          renderRow={renderRtpRow}
          isLoadingData={props.isLoadingData}
          isEditing={!props.locked}
          defaultSortColumn={"mediaProductHeaderCode"}
          infoKey={"hydrationErrors"}
          infoMsg={getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_INFO_TOOLTIP')}
          columnErrors={columnErrors}
          loadingColumns={loadingColumns}
        />
        :
        <DynamicTable
          key="swProductMappingTable"
          columns={swColumnConfig(resources, hasFulfillmentSeason, props.showHydrationErrors)}
          data={swMappingEntitlements}
          isLoadingData={props.isLoadingData}
          isEditing={!props.locked}
          renderRow={renderSwRow}
          // productType is not a rendered column here, so it gave an order with no header to explain it
          defaultSortColumn={"product"}
          infoKey={"hydrationErrors"}
          infoMsg={getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_INFO_TOOLTIP')}
          columnErrors={columnErrors}
          loadingColumns={loadingColumns}
        />
      }
    </div>
  );
}
